import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, Form, Button, Modal } from 'react-bootstrap';
import { useAuth } from '../services/AuthContext';
import { registerUser } from '../services/firebase';
import { toast } from 'react-toastify';
import './RegisterModal.css';
import { collection, getDocs, query, where, addDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import * as XLSX from 'xlsx';
import { doc, deleteDoc, updateDoc, getDoc } from 'firebase/firestore';

const RegisterModal = ({ show, onClose }) => {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newUser, setNewUser] = useState({
    userId: "",
    password: "",
    level: "0",
    server: "",
    name: "",
    servers_access: []
  });
  const [servers, setServers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showPassword, setShowPassword] = useState(false);
  const [filterServer, setFilterServer] = useState('all');
  const [showPasswords, setShowPasswords] = useState(false);
  const { logout } = useAuth();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchServers();
    fetchUsers();
  }, []);

  const fetchServers = async () => {
    try {
      const response = await axios.get('https://radio.amgroupmkt.com:1030/api/v2/servers/');
      setServers(response.data);
    } catch (error) {
      console.error('Error fetching server data:', error);
      toast.error('Error al cargar servidores');
    }
  };

  const fetchUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const querySnapshot = await getDocs(usersRef);
      const usersList = querySnapshot.docs.map(doc => ({
        userId: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error al cargar usuarios');
    }
  };

  const handleEdit = (user) => {
    setNewUser({
      userId: user.userId,
      password: user.password,
      level: user.level,
      server: user.server,
      name: user.name,
      servers_access: user.servers_access || []
    });
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!userToDelete || !userToDelete.userId) {
      toast.error('Error: Usuario no válido para eliminar');
      return;
    }

    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('userId', '==', userToDelete.userId));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        toast.error('Error: El usuario no existe en la base de datos');
        return;
      }

      await deleteDoc(doc(db, 'users', querySnapshot.docs[0].id));
      toast.success('Usuario eliminado exitosamente');
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      await fetchUsers();
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      toast.error('Error al eliminar usuario');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar que servers_access sea un array
    if (!Array.isArray(newUser.servers_access)) {
      newUser.servers_access = [];
    }

    let result;
    
    if (isEditing) {
      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('userId', '==', newUser.userId));
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          toast.error('Error: El usuario no existe en la base de datos');
          return;
        }

        const userData = {
          userId: newUser.userId,
          password: newUser.password,
          name: newUser.name,
          server: newUser.server,
          level: newUser.level,
          servers_access: newUser.servers_access
        };

        await updateDoc(doc(db, 'users', querySnapshot.docs[0].id), userData);
        result = { success: true };
      } catch (error) {
        console.error('Error al actualizar usuario:', error);
        result = { success: false, message: 'Error al actualizar usuario' };
      }
    } else {
      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('userId', '==', newUser.userId));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          toast.error('Error: El ID de usuario ya existe');
          return;
        }

        const docRef = await addDoc(collection(db, 'users'), {
          userId: newUser.userId,
          password: newUser.password,
          name: newUser.name,
          server: newUser.server,
          level: newUser.level,
          servers_access: newUser.servers_access
        });
        result = { success: true };
      } catch (error) {
        console.error('Error al registrar usuario:', error);
        result = { success: false, message: 'Error al registrar usuario' };
      }
    }

    if (result.success) {
      toast.success(isEditing ? "¡Usuario actualizado exitosamente!" : "¡Usuario registrado exitosamente!");
      setShowModal(false);
      setNewUser({
        userId: "",
        password: "",
        level: "0",
        server: "",
        name: "",
        servers_access: []
      });
      setIsEditing(false);
      await fetchUsers();
    } else {
      toast.error(result.message || "Error al procesar usuario");
    }
  };

  const filteredUsers = users.filter(user => {
    const searchFields = [
      user.userId?.toLowerCase() || '',
      user.name?.toLowerCase() || '',
      getLevelName(user.level)?.toLowerCase() || ''
    ];
    
    const matchesSearch = searchTerm === '' || searchFields.some(field => 
      field.includes(searchTerm.toLowerCase())
    );
    
    const matchesLevel = filterLevel === 'all' || Number(user.level) === Number(filterLevel);
    const matchesServer = filterServer === 'all' || user.server === filterServer;
    
    return matchesSearch && matchesLevel && matchesServer;
  });

  const paginatedUsers = filteredUsers.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );

  const handleDownloadExcel = () => {
    try {
      const dataToExport = filteredUsers.map(user => ({
        'ID Usuario': user.userId,
        'Sucursal': user.name,
        'Nivel': getLevelName(user.level),
        'Servidor Principal': user.server,
        'Acceso a Servidores': user.servers_access ? user.servers_access.map(url => 
          new URL(url).pathname.substring(new URL(url).pathname.lastIndexOf('/') + 1)
        ).join(', ') : '-',
        'Contraseña': showPasswords ? user.password : '••••••••'
      }));

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');
      
      const fileName = `usuarios_sistema_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      XLSX.writeFile(wb, fileName);
      
      toast.success('Archivo descargado exitosamente');
    } catch (error) {
      console.error('Error al descargar Excel:', error);
      toast.error('Error al descargar el archivo');
    }
  };

  return (
    <div className={`register-modal-container ${show ? 'show' : ''}`}>
      <div className="register-header">
        <h2>Gestión de Usuarios del Sistema</h2>
        <div className="register-actions">
          <div className="search-box">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              placeholder="Buscar usuario..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="level-filter"
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value)}
          >
            <option value="all">Todos los niveles</option>
            <option value="0">S: Sistemas</option>
            <option value="1">A++: Administrador</option>
            <option value="2">A: Usuario</option>
          </select>
          <select
            className="server-filter"
            value={filterServer}
            onChange={(e) => setFilterServer(e.target.value)}
          >
            <option value="all">Todos los servidores</option>
            {servers.map((s, index) => (
              <option key={index} value={s.url_address}>
                {s.name || s.url_address}
              </option>
            ))}
          </select>
          <Button 
            className="download-btn"
            onClick={handleDownloadExcel}
            title="Descargar datos filtrados"
          >
            <i className="fas fa-file-excel"></i>
            Descargar Excel
          </Button>
          <Button 
            className="add-user-btn"
            onClick={() => setShowModal(true)}
          >
            <i className="fas fa-user-plus"></i>
            Nuevo Usuario
          </Button>
        </div>
      </div>

      <div className="users-table-container">
        <Table hover className="users-table">
          <thead>
            <tr>
              <th>ID Usuario</th>
              <th>Sucursal</th>
              <th>Nivel</th>
              <th>Servidor Principal</th>
              <th>Acceso a Servidores</th>
              <th>
                Contraseña
                <button
                  type="button"
                  className="password-toggle-all"
                  onClick={() => setShowPasswords(!showPasswords)}
                  title={showPasswords ? "Ocultar contraseñas" : "Mostrar contraseñas"}
                >
                  <i className={`fas ${showPasswords ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.map((user, index) => (
              <tr key={index}>
                <td>{user.userId}</td>
                <td>{user.name}</td>
                <td>{getLevelName(user.level)}</td>
                <td>{user.server}</td>
                <td>
                  {user.servers_access ? (
                    <div 
                      style={{ 
                        maxWidth: '150px', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'help'
                      }}
                      title={user.servers_access.map(url => {
                        try {
                          if (!url) return '';
                          const urlObj = new URL(url);
                          return urlObj.pathname.substring(urlObj.pathname.lastIndexOf('/') + 1);
                        } catch (error) {
                          console.error('Invalid URL:', url);
                          return url;
                        }
                      }).filter(Boolean).join('\n')}
                    >
                      {user.servers_access.length} servidor{user.servers_access.length !== 1 ? 'es' : ''}
                    </div>
                  ) : '-'}
                </td>
                <td>
                  {showPasswords ? user.password : '••••••••'}
                </td>
                <td>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleEdit(user)}
                      title="Editar usuario"
                    >
                      <i className="fas fa-edit"></i>
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => {
                        setUserToDelete(user);
                        setShowDeleteConfirm(true);
                      }}
                      title="Eliminar usuario"
                    >
                      <i className="fas fa-trash-alt"></i>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="register-footer">
        <div className="rows-per-page">
          <span>Mostrar:</span>
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(0);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <span>registros</span>
        </div>
        <div className="pagination-controls">
          <Button
            className="pagination-btn"
            onClick={() => setCurrentPage(0)}
            disabled={currentPage === 0}
            title="Primera página"
          >
            <i className="fas fa-step-backward"></i>
          </Button>
          <Button
            className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            title="Página anterior"
          >
            <i className="fas fa-chevron-left"></i>
          </Button>
          <span className="page-info">
            Página {currentPage + 1} de {Math.ceil(filteredUsers.length / rowsPerPage)}
          </span>
          <Button
            className="pagination-btn"
            onClick={() => setCurrentPage(p => p + 1)}
            disabled={(currentPage + 1) * rowsPerPage >= filteredUsers.length}
            title="Página siguiente"
          >
            <i className="fas fa-chevron-right"></i>
          </Button>
          <Button
            className="pagination-btn"
            onClick={() => setCurrentPage(Math.ceil(filteredUsers.length / rowsPerPage) - 1)}
            disabled={(currentPage + 1) * rowsPerPage >= filteredUsers.length}
            title="Última página"
          >
            <i className="fas fa-step-forward"></i>
          </Button>
        </div>
      </div>

      {/* Modal de confirmación de eliminación */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Está seguro que desea eliminar al usuario {userToDelete?.name}?
          <br />
          <small className="text-danger">Esta acción no se puede deshacer.</small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Eliminar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de registro/edición */}
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setIsEditing(false);
          setNewUser({
            userId: "",
            password: "",
            level: "0",
            server: "",
            name: "",
            servers_access: []
          });
        }}
        centered
        className="custom-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'Editar Usuario' : 'Nuevo Usuario del Sistema'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>ID de Usuario</Form.Label>
              <Form.Control
                type="text"
                value={newUser.userId}
                onChange={(e) => setNewUser({...newUser, userId: e.target.value})}
                required
                disabled={isEditing}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Contraseña</Form.Label>
              <div className="password-input-group">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nombre de Sucursal</Form.Label>
              <Form.Control
                type="text"
                value={newUser.name}
                onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nivel</Form.Label>
              <Form.Select
                value={newUser.level}
                onChange={(e) => setNewUser({...newUser, level: e.target.value})}
                required
              >
                <option value="0">S: Sistemas</option>
                <option value="1">A++: Administrador de marca</option>
                <option value="2">A: Usuario</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Servidor Principal</Form.Label>
              <Form.Select
                value={newUser.server}
                onChange={(e) => setNewUser({...newUser, server: e.target.value})}
                required
              >
                <option value="">Seleccione un servidor</option>
                {servers.map((s, index) => (
                  <option key={index} value={s.url_address}>
                    {s.name || s.url_address}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Acceso a Servidores</Form.Label>
              <div className="servers-checkbox-container" style={{ maxHeight: '200px', overflowY: 'auto', padding: '10px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                {servers.map((s, index) => (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    id={`server-${index}`}
                    label={s.name || s.url_address}
                    checked={newUser.servers_access.includes(s.url_address)}
                    onChange={(e) => {
                      const updatedAccess = e.target.checked
                        ? [...newUser.servers_access, s.url_address]
                        : newUser.servers_access.filter(url => url !== s.url_address);
                      setNewUser({...newUser, servers_access: updatedAccess});
                    }}
                    className="mb-2"
                  />
                ))}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {isEditing ? 'Actualizar Usuario' : 'Registrar Usuario'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const getLevelName = (level) => {
  const levels = {
    "0": "S: Sistemas",
    "1": "A++: Administrador",
    "2": "A: Usuario"
  };
  return levels[level] || level;
};

export default RegisterModal;
